import { selectStyles } from '@astro/astrologer-forms'

export const PLANET_GLYPHS = {
  Sun: '☉', Moon: '☽', Mars: '♂', Mercury: '☿', Jupiter: '♃',
  Venus: '♀', Saturn: '♄', Rahu: '☊', Ketu: '☋',
}

/* Shared react-select skin, tuned to the Astro-Tools `.field` surface. */
export const ksSelectStyles = {
  ...selectStyles,
  control: (base, state) => ({
    ...selectStyles.control(base, state),
    background: '#fff',
    borderColor: state.isFocused ? 'rgba(245,158,11,.55)' : 'rgba(120,53,15,.14)',
    borderWidth: 1,
    borderRadius: 14,
    minHeight: 46,
    fontSize: 13,
    boxShadow: state.isFocused ? '0 0 0 3px rgba(245,158,11,.12)' : 'none',
  }),
  menu: (base) => ({ ...base, zIndex: 25, borderRadius: 14, overflow: 'hidden', fontSize: 13 }),
}

const BADGE_TONES = {
  gold: 'ks-badge--gold',
  green: 'ks-badge--green',
  red: 'ks-badge--red',
  amber: 'ks-badge--amber',
  muted: 'ks-badge--muted',
}

export function Badge({ tone = 'muted', children }) {
  return <span className={`ks-badge ${BADGE_TONES[tone] || BADGE_TONES.muted}`}>{children}</span>
}

/** Labelled key→value row (the studio's own take on the page's ResultRow). */
export function InfoRow({ label, value, accent }) {
  return (
    <div className="ks-row">
      <span className="ks-row-label">{label}</span>
      <span className={`ks-row-value${accent ? ' ks-row-accent' : ''}`}>{value}</span>
    </div>
  )
}

/** Small pill toggle group (chart style, sub-views). */
export function PillGroup({ options, value, onChange, ariaLabel }) {
  return (
    <div className="ks-pills" role="group" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          className={`ks-pill${value === o.key ? ' active' : ''}`}
          onClick={() => onChange(o.key)}
          aria-pressed={value === o.key}
        >
          {o.icon && <span className="ks-pill-ico">{o.icon}</span>}
          {o.label}
        </button>
      ))}
    </div>
  )
}

export function StudioEmpty({ icon, text }) {
  return (
    <div className="ks-empty">
      <div className="ks-empty-icon">{icon}</div>
      <p>{text}</p>
    </div>
  )
}

export function StudioError({ text, onRetry }) {
  return (
    <div className="ks-empty">
      <div className="ks-empty-icon">🔭</div>
      <p>{text}</p>
      {onRetry && (
        <button type="button" className="ks-retry" onClick={onRetry}>Retry</button>
      )}
    </div>
  )
}

/** Shimmer table placeholder while a paid call is in flight. */
export function TableSkeleton({ rows = 9, cols = 6 }) {
  return (
    <div className="ks-table-wrap animate-pulse" aria-hidden="true">
      <table className="ks-table">
        <thead>
          <tr>
            {Array.from({ length: cols }).map((_, c) => (
              <th key={c}><span className="ks-skel ks-skel-th" /></th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, r) => (
            <tr key={r}>
              {Array.from({ length: cols }).map((_, c) => (
                <td key={c}><span className="ks-skel" style={{ width: `${52 + ((r + c) % 3) * 22}px` }} /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Shimmer card-grid placeholder (doshas, yogas, report). */
export function CardsSkeleton({ cards = 6 }) {
  return (
    <div className="ks-card-grid animate-pulse" aria-hidden="true">
      {Array.from({ length: cards }).map((_, i) => (
        <div className="ks-card" key={i}>
          <span className="ks-skel" style={{ width: '42%' }} />
          <span className="ks-skel" style={{ width: '92%', marginTop: 12 }} />
          <span className="ks-skel" style={{ width: '84%', marginTop: 8 }} />
          <span className="ks-skel" style={{ width: '58%', marginTop: 8 }} />
        </div>
      ))}
    </div>
  )
}

/**
 * First-load placeholder for the chart canvas — a shimmer square under a
 * spinner. Held until BOTH the grid SVG and the rashi-number data have
 * arrived, so the chart never appears un-numbered and then re-draws.
 */
export function ChartSkeleton() {
  return (
    <div className="ks-chart-loader" role="status" aria-live="polite">
      <div className="ks-chart-frame animate-pulse" aria-hidden="true">
        <div className="ks-chart-skel" />
      </div>
      <div className="ks-chart-overlay">
        <span className="ks-spinner" />
        <p>Casting your chart…</p>
      </div>
    </div>
  )
}
